"use client";

import {useEffect, useRef, type ReactNode} from "react";
import {createPortal} from "react-dom";
import clsx from "clsx";
import PixelGridLoader from "@/components/common/PixelGridLoader";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {useArtifactPanel, useAutoOpenArtifact} from "@/contexts/chat/ArtifactPanelContext";

/** Keep live workflow actions connected to the chat while using the shared artifact surface. */
export default function WorkflowArtifact({id, title, summary, autoOpen, busy = false, children}: {
   id: string;
   title: string;
   summary: string;
   autoOpen: boolean;
   busy?: boolean;
   children: ReactNode;
}) {
   const {activeArtifact, openArtifact, closeArtifact, workflowPanelHost} = useArtifactPanel();
   const isOpen = activeArtifact?.kind === "workflows" && activeArtifact.id === id;
   const buildArtifact = () => ({kind: "workflows" as const, id, title});
   useAutoOpenArtifact(autoOpen, id, buildArtifact, openArtifact);
   const mountedId = useRef<string | null>(null);
   useEffect(() => {
      mountedId.current = id;
      return () => {
         mountedId.current = null;
         // Strict Mode replays effects without removing the chat. Only close
         // after an actual unmount or a switch to another conversation.
         queueMicrotask(() => {
            if (mountedId.current !== id) closeArtifact(id);
         });
      };
   }, [closeArtifact, id]);

   return <>
      {(!isOpen || !workflowPanelHost) && <div className="mx-3 my-4 md:mx-6">
         <button
            type="button"
            aria-label={title}
            aria-expanded={isOpen}
            onClick={() => openArtifact(buildArtifact())}
            className="group flex w-full items-center gap-3 rounded-xl border border-300 bg-panel px-4 py-3 text-left transition-all hover:border-primary/40 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
         >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
               {busy ? <PixelGridLoader /> : <Icon icon="diagram-project" size={ICON_SIZES.SM} />}
            </span>
            <span className="min-w-0 flex-1">
               <span className="block text-sm font-medium text-900">{title}</span>
               {summary && <span className={clsx("mt-0.5 block truncate text-xs text-500", busy && "w-fit max-w-full animate-text-shimmer motion-reduce:animate-none motion-reduce:[-webkit-text-fill-color:currentColor]")} title={summary}>{summary}</span>}
            </span>
            <Icon icon="arrow-up-right-from-square" size={ICON_SIZES.SM} className="shrink-0 text-500 transition-colors group-hover:text-primary" />
         </button>
      </div>}
      {isOpen && workflowPanelHost && createPortal(children, workflowPanelHost)}
   </>;
}
